import { logger } from '@/shared/utils/logger';

/**
 * Viewport Utility
 * Handles viewport orientation changes and fixes for Android devices
 * Addresses Android browser bug where viewport doesn't properly resize on rotation
 */

interface ViewportConfig {
  enableAndroidFix?: boolean;
  debounceMs?: number;
}

class ViewportManager {
  private config: ViewportConfig;
  private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  private isAndroid: boolean;
  private isPWA: boolean;
  private orientationChangeListeners = new Set<() => void>();

  constructor(config: ViewportConfig = {}) {
    this.config = {
      enableAndroidFix: true,
      debounceMs: 200,
      ...config,
    };

    this.isAndroid = this.detectAndroid();
    this.isPWA = this.detectPWA();
  }

  /**
   * Detect if device is Android
   */
  private detectAndroid(): boolean {
    const isAndroid = /Android/i.test(navigator.userAgent);
    logger.info('Device detection:', {
      isAndroid,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
    });
    return isAndroid;
  }

  /**
   * Detect if app is running as an installed PWA (standalone mode)
   */
  private detectPWA(): boolean {
    // Check if running in standalone mode (installed PWA)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error - navigator.standalone is iOS-specific
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');

    logger.info('PWA detection:', {
      isStandalone,
      displayMode: window.matchMedia('(display-mode: standalone)').matches,
      // @ts-expect-error - navigator.standalone is iOS-specific
      navigatorStandalone: window.navigator.standalone,
    });

    return isStandalone;
  }

  /**
   * Force viewport refresh by multiple methods
   * This is a known workaround for Android viewport orientation bug
   */
  private forceViewportRefresh(): void {
    if (!this.config.enableAndroidFix || !this.isAndroid) return;

    try {
      const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
      if (!viewport) {
        logger.warn('Viewport meta tag not found');
        return;
      }

      const originalContent = viewport.getAttribute('content') || '';

      // Method 1: Remove and re-add the viewport tag (most aggressive)
      const parent = viewport.parentNode;
      if (parent) {
        parent.removeChild(viewport);

        // Force reflow
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        document.body.offsetHeight;

        // Re-add with original content
        parent.appendChild(viewport);
        viewport.setAttribute('content', originalContent);
      }

      // Method 2: Toggle initial-scale as backup
      setTimeout(() => {
        const newContent = originalContent.replace('initial-scale=1.0', 'initial-scale=1.001');
        viewport.setAttribute('content', newContent);

        // Force another reflow
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        document.body.offsetHeight;

        // Restore original
        setTimeout(() => {
          viewport.setAttribute('content', originalContent);
          logger.info('🔄 Viewport refreshed for Android orientation change');
        }, 50);
      }, 50);

      logger.info('🤖 Applying Android viewport fix...');
    } catch (error) {
      logger.error('Error refreshing viewport:', error);
    }
  }

  /**
   * Handle orientation/resize change
   */
  private handleOrientationChange = (): void => {
    // Clear existing timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Debounce the resize handler (longer delay for Android)
    const delay = this.isAndroid ? 300 : this.config.debounceMs || 200;

    this.resizeTimeout = setTimeout(() => {
      const orientation = this.getOrientation();
      const width = window.innerWidth;
      const height = window.innerHeight;

      logger.info('📱 Orientation changed', {
        orientation,
        width,
        height,
        isAndroid: this.isAndroid,
        screenOrientation: window.screen.orientation?.type || 'unknown',
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
      });

      // Force refresh for Android
      if (this.isAndroid) {
        this.forceViewportRefresh();
      }

      // Notify listeners
      this.notifyListeners();
    }, delay);
  };

  /**
   * Get current orientation
   */
  getOrientation(): 'portrait' | 'landscape' {
    // Use screen.orientation API if available (modern browsers)
    if (window.screen.orientation) {
      return window.screen.orientation.type.includes('portrait') ? 'portrait' : 'landscape';
    }

    // Fallback to window dimensions
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  }

  /**
   * Check if device is in portrait mode
   */
  isPortrait(): boolean {
    return this.getOrientation() === 'portrait';
  }

  /**
   * Check if device is in landscape mode
   */
  isLandscape(): boolean {
    return this.getOrientation() === 'landscape';
  }

  /**
   * Add listener for orientation changes
   */
  addOrientationChangeListener(callback: () => void): () => void {
    this.orientationChangeListeners.add(callback);

    // Return cleanup function
    return () => {
      this.orientationChangeListeners.delete(callback);
    };
  }

  /**
   * Notify all orientation change listeners
   */
  private notifyListeners(): void {
    this.orientationChangeListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        logger.error('Error in orientation change listener:', error);
      }
    });
  }

  /**
   * Check if running as PWA
   */
  isPWAMode(): boolean {
    return this.isPWA;
  }

  /**
   * Initialize viewport manager
   * Attaches event listeners for orientation changes
   */
  initialize(): () => void {
    logger.info('Viewport manager initialized', {
      isAndroid: this.isAndroid,
      isPWA: this.isPWA,
      orientation: this.getOrientation(),
      screenOrientationType: window.screen.orientation?.type || 'unknown',
    });

    // Listen to both orientationchange and resize events
    // - orientationchange: fired when device orientation changes
    // - resize: more reliable on Android, fires after orientation change completes
    window.addEventListener('orientationchange', this.handleOrientationChange);
    window.addEventListener('resize', this.handleOrientationChange);

    // For PWA mode, also listen to screen.orientation change event if available
    if (this.isPWA && window.screen.orientation) {
      window.screen.orientation.addEventListener('change', this.handleOrientationChange);
      logger.info('Added screen.orientation listener for PWA mode');
    }

    // Return cleanup function
    return () => {
      window.removeEventListener('orientationchange', this.handleOrientationChange);
      window.removeEventListener('resize', this.handleOrientationChange);

      // Remove screen.orientation listener if it was added
      if (this.isPWA && window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', this.handleOrientationChange);
      }

      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.orientationChangeListeners.clear();
      logger.debug('Viewport manager cleaned up');
    };
  }
}

// Export singleton instance
export const viewportManager = new ViewportManager();

// Export utility functions
export const getOrientation = () => viewportManager.getOrientation();
export const isPortrait = () => viewportManager.isPortrait();
export const isLandscape = () => viewportManager.isLandscape();
export const isPWAMode = () => viewportManager.isPWAMode();
